import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import {
  ADVICEPREP_PAYMENT_RETRY_DELAY_MS,
  advicePrepPaymentRequiredReason,
  isWithinAdvicePrepPaymentWindow,
  reportAdvicePrepPaymentRequired,
} from '@/features/adviceprep/paywall/paymentRequired'
import { isAdvisoryAccessDenied } from '@/features/advisory-ai/access'

const defaultQueryRetries = 3

function shouldRetry(failureCount: number, error: unknown, fallback: boolean) {
  // A permission denial is settled. Retrying it only spends requests to be told no again.
  if (isAdvisoryAccessDenied(error)) return false
  if (!advicePrepPaymentRequiredReason(error)) return fallback
  return failureCount < 1 && isWithinAdvicePrepPaymentWindow()
}

export function defaultQueryRetry(failureCount: number, error: unknown) {
  return shouldRetry(failureCount, error, failureCount < defaultQueryRetries)
}

function retryDelay(attemptIndex: number, error: unknown) {
  if (advicePrepPaymentRequiredReason(error)) return ADVICEPREP_PAYMENT_RETRY_DELAY_MS
  return Math.min(1000 * 2 ** attemptIndex, 30000)
}

function handleError(error: unknown) {
  const reason = advicePrepPaymentRequiredReason(error)
  if (reason) reportAdvicePrepPaymentRequired(reason)
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleError }),
  mutationCache: new MutationCache({ onError: handleError }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: defaultQueryRetry,
      retryDelay,
    },
    mutations: {
      retry: (failureCount, error) => shouldRetry(failureCount, error, false),
      retryDelay,
    },
  },
})
